import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr, fmtDate, fileToBase64, dataUrl } from "@/lib/api";
import { useLang } from "@/i18n";
import { MessageCircle, X, Paperclip, Send, FileText, Headset } from "lucide-react";

function AttachmentView({ att }) {
  if (!att) return null;
  if (att.mime?.startsWith("image/")) {
    return (
      <a href={dataUrl(att.mime, att.data)} target="_blank" rel="noreferrer">
        <img src={dataUrl(att.mime, att.data)} alt={att.name}
             className="mt-2 rounded-lg max-w-[220px] border border-white/20" />
      </a>
    );
  }
  return (
    <a href={dataUrl(att.mime, att.data)} download={att.name}
       className="mt-2 inline-flex items-center gap-1.5 text-xs underline opacity-90">
      <FileText size={13} /> {att.name}
    </a>
  );
}

export default function ChatWidget() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/client/chat").then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, open ? 4000 : 15000);
    return () => clearInterval(iv);
  }, [open, load]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !file) return;
    setSending(true);
    try {
      let attachment = null;
      if (file) attachment = await fileToBase64(file);
      await api.post("/client/chat", { text: text.trim(), attachment });
      setText("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-open-btn"
        onClick={() => setOpen(!open)}
        className="fixed bottom-5 right-5 z-50 h-14 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#635BFF] text-white px-5 flex items-center gap-2 font-semibold text-sm shadow-2xl shadow-[#635BFF]/40 hover:scale-105 active:scale-95 transition-transform"
      >
        {open ? <X size={18} /> : <MessageCircle size={18} />}
        <span className="hidden sm:inline">{t("chat.open")}</span>
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-5 z-50 w-[92vw] max-w-sm h-[480px] bg-white border border-[#E6EBF1] rounded-2xl shadow-2xl shadow-[#0A2540]/25 flex flex-col overflow-hidden animate-fade-up"
        >
          <div className="px-4 py-3 bg-gradient-to-r from-[#7C3AED] to-[#635BFF] flex items-center gap-2.5">
            <Headset size={18} className="text-white" />
            <div>
              <p className="text-sm font-semibold text-white">{t("chat.title")}</p>
              <p className="text-[10px] text-white/70 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse-dot" />
                UCP Desk
              </p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F6F9FC]" data-testid="chat-messages">
            {messages.map((m) => {
              const mine = m.sender === "client";
              const body = m.auto ? t("chat.autoReply") : m.text;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      mine
                        ? "bg-[#635BFF] text-white rounded-br-md"
                        : "bg-white border border-[#E6EBF1] text-[#0A2540] rounded-bl-md"
                    } ${m.auto ? "italic" : ""}`}
                  >
                    {body && <p className="whitespace-pre-line">{body}</p>}
                    <AttachmentView att={m.attachment} />
                    <p className={`text-[9px] mt-1 font-ticker ${mine ? "text-white/60" : "text-slate-400"}`}>
                      {fmtDate(m.created_at)} {m.edited ? t("chat.edited") : ""}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={send} className="p-3 bg-white border-t border-[#E6EBF1]">
            {file && (
              <p className="text-xs text-[#635BFF] mb-2 flex items-center gap-1.5">
                <Paperclip size={12} /> {file.name}
                <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                        className="text-red-500 ml-1"><X size={12} /></button>
              </p>
            )}
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" className="hidden" data-testid="chat-file-input"
                     onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <button type="button" data-testid="chat-attach-btn" title={t("chat.attach")}
                      onClick={() => fileRef.current?.click()}
                      className="w-9 h-9 rounded-full border border-[#E6EBF1] flex items-center justify-center text-[#425466] hover:text-[#635BFF] hover:border-[#635BFF]/40 transition-colors shrink-0">
                <Paperclip size={15} />
              </button>
              <input
                data-testid="chat-text-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t("chat.placeholder")}
                className="flex-1 h-9 px-3.5 rounded-full border border-[#E6EBF1] text-sm text-[#0A2540] outline-none focus:border-[#635BFF]/50 bg-[#F6F9FC]"
              />
              <button type="submit" data-testid="chat-send-btn" disabled={sending}
                      className="w-9 h-9 rounded-full bg-[#635BFF] text-white flex items-center justify-center hover:bg-[#0A2540] transition-colors shrink-0 disabled:opacity-50">
                <Send size={15} />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
